<template>
  <div
    class="content__item content__item--social-share"
    :class="`content__item--social-share--${model.state.settings.alignment}`"
  >
    <a
      v-if="model.state.settings.isFacebookActive"
      :tabindex="hasTabIndex ? 0 : -1"
      class="content__item-social-share content__item-social-share--facebook"
      :href="model.state.settings.facebookUrl"
      :style="elementStyling"
      target="_blank"
      role="button"
      @click="onClick(model.state.settings.facebookOriginalUrl)"
    >
      <Icon class="content__item-social-icon" icon="facebook" :style="iconStyles" />
      <span class="sr-only">Facebook</span>
    </a>

    <a
      v-if="model.state.settings.isTwitterActive"
      :tabindex="hasTabIndex ? 0 : -1"
      class="content__item-social-share content__item-social-share--twitter"
      :href="model.state.settings.twitterUrl"
      :style="elementStyling"
      target="_blank"
      role="button"
      @click="onClick(model.state.settings.twitterOriginalUrl)"
    >
      <Icon class="content__item-social-icon" icon="x_logo" :style="iconStyles" />
      <span class="sr-only">X</span>
    </a>

    <a
      v-if="model.state.settings.isInstagramActive"
      :tabindex="hasTabIndex ? 0 : -1"
      class="content__item-social-share content__item-social-share--instagram"
      :href="model.state.settings.instagramUrl"
      :style="elementStyling"
      target="_blank"
      role="button"
      @click="onClick(model.state.settings.instagramOriginalUrl)"
    >
      <Icon class="content__item-social-icon" icon="instagram" :style="iconStyles" />
      <span class="sr-only">Instagram</span>
    </a>

    <a
      v-if="model.state.settings.isLinkedinActive"
      :tabindex="hasTabIndex ? 0 : -1"
      class="content__item-social-share content__item-social-share--linkedin"
      :href="model.state.settings.linkedinUrl"
      :style="elementStyling"
      target="_blank"
      role="button"
      @click="onClick(model.state.settings.linkedinOriginalUrl)"
    >
      <Icon class="content__item-social-icon" icon="linkedin" :style="iconStyles" />
      <span class="sr-only">Linkedin</span>
    </a>

    <a
      v-if="model.state.settings.isYoutubeActive"
      :tabindex="hasTabIndex ? 0 : -1"
      class="content__item-social-share content__item-social-share--youtube"
      :href="model.state.settings.youtubeUrl"
      :style="elementStyling"
      target="_blank"
      role="button"
      @click="onClick(model.state.settings.youtubeOriginalUrl)"
    >
      <Icon class="content__item-social-icon" icon="youtube" :style="iconStyles" />
      <span class="sr-only">Youtube</span>
    </a>

    <a
      v-if="model.state.settings.isVimeoActive"
      :tabindex="hasTabIndex ? 0 : -1"
      class="content__item-social-share content__item-social-share--vimeo"
      :href="model.state.settings.vimeoUrl"
      :style="elementStyling"
      target="_blank"
      role="button"
      @click="onClick(model.state.settings.vimeoOriginalUrl)"
    >
      <Icon class="content__item-social-icon" icon="vimeo" :style="iconStyles" />
      <span class="sr-only">Vimeo</span>
    </a>

    <a
      v-if="model.state.settings.isPinterestActive"
      :tabindex="hasTabIndex ? 0 : -1"
      class="content__item-social-share content__item-social-share--pinterest"
      :href="model.state.settings.pinterestUrl"
      :style="elementStyling"
      target="_blank"
      role="button"
      @click="onClick(model.state.settings.pinterestOriginalUrl)"
    >
      <Icon class="content__item-social-icon" icon="pinterest" :style="iconStyles" />
      <span class="sr-only">Pinterest</span>
    </a>

    <a
      v-if="model.state.settings.isWebsiteActive"
      :tabindex="hasTabIndex ? 0 : -1"
      class="content__item-social-share content__item-social-share--home"
      :href="model.state.settings.websiteUrl"
      :style="elementStyling"
      target="_blank"
      role="button"
      @click="onClick(model.state.settings.websiteOriginalUrl)"
    >
      <Icon class="content__item-social-icon" icon="home" :style="iconStyles" />
      <span class="sr-only">Home</span>
    </a>

    <a
      v-if="model.state.settings.isTikTokActive"
      :tabindex="hasTabIndex ? 0 : -1"
      class="content__item-social-share content__item-social-share--tiktok"
      :href="model.state.settings.tiktokUrl"
      :style="elementStyling"
      target="_blank"
      role="button"
      @click="onClick(model.state.settings.tiktokOriginalUrl)"
    >
      <Icon class="content__item-social-icon" icon="tiktok" :style="iconStyles" />
      <span class="sr-only">TikTok</span>
    </a>

    <a
      v-if="model.state.settings.isOtherActive"
      :tabindex="hasTabIndex ? 0 : -1"
      class="content__item-social-share content__item-social-share--globe"
      :href="model.state.settings.otherUrl"
      :style="elementStyling"
      target="_blank"
      role="button"
      @click="onClick(model.state.settings.otherOriginalUrl)"
    >
      <img
        v-if="model.state.settings.otherImageUrl"
        :src="model.state.settings.otherImageUrl"
        alt="custom share image"
        class="content__item-social-icon"
        :style="customImageStyle"
      />
      <i
        v-else-if="model.state.settings.otherIcon"
        class="content__item-social-icon fa"
        :class="model.state.settings.otherIcon"
        :style="iconStyles"
      ></i>
      <Icon v-else class="content__item-social-icon" icon="globe" :style="iconStyles" />
      <span class="sr-only">Web</span>
    </a>
  </div>
</template>

<script lang="ts">
import type { CSSProperties, PropType } from 'vue';
import { computed, defineComponent } from 'vue';
import type { AddonShareModel } from '@/src/components/addons/share/Model';
import useLeadScore from '@/src/hooks/useLeadscore';
import { useCampaignStore } from '@/src/store/campaign';
import Icon from '@/src/components/icons/Icon.vue';

export default defineComponent({
  name: 'AddonShare',
  components: { Icon },
  inheritAttrs: false,
  props: {
    model: {
      type: Object as PropType<AddonShareModel>,
      required: true
    }
  },
  setup(props) {
    const campaignStore = useCampaignStore();
    const campaignState = campaignStore.model?.state;

    const elementStyling = computed<CSSProperties>(() => {
      const calcIconSize = props.model.state.settings.iconSize
        ? Math.round(props.model.state.settings.iconSize * 1.7)
        : 0; // old project used this calculated size

      const calcMarginSize = props.model.state.settings.iconSize
        ? Math.round(props.model.state.settings.iconSize / 5) / 10
        : 0; // old project used this calculated size

      return {
        borderRadius: props.model.state.settings.roundedButtons
          ? typeof props.model.state.settings.borderRadius !== 'undefined'
            ? props.model.state.settings.borderRadius
            : '50%'
          : '0',

        ...(props.model.state.settings.iconSize && {
          height: `${calcIconSize}px`,
          width: `${calcIconSize}px`,
          lineHeight: `${calcIconSize}px`,
          fontSize: `${props.model.state.settings.iconSize}px`,
          marginLeft: `${calcMarginSize}rem`,
          marginRight: `${calcMarginSize}rem`,
          backgroundColor: props.model.state.settings.iconBackgroundColor || 'transparent'
        })
      };
    });

    const iconStyles = computed<CSSProperties>(() => {
      return {
        ...(props.model.state.settings.iconColor && { color: `${props.model.state.settings.iconColor}!important` })
      };
    });

    const customImageStyle = computed<CSSProperties>(() => {
      return {
        height: `${props.model.state.settings.iconSize}px`,
        width: `${props.model.state.settings.iconSize}px`
      };
    });

    const isEditModeActive = computed(() => {
      return campaignStore.model?.state.isEditModeActive;
    });

    const hasTabIndex = computed(() => {
      return !isEditModeActive.value;
    });

    const onClick = (originalUrl: string) => {
      if (campaignState?.config?.leadScoreEnabled && originalUrl) {
        useLeadScore().track({
          inSession: true,
          object: 'LINK',
          object_value: originalUrl,
          distinct: true
        });
      }
    };

    return {
      onClick,
      elementStyling,
      iconStyles,
      customImageStyle,
      hasTabIndex
    };
  }
});
</script>

<style lang="scss">
.grid__addon--share {
  .content__item--social-share {
    margin-top: 0;
    margin-bottom: 0;

    img {
      border-radius: inherit;
      position: absolute;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
    }

    &--left {
      text-align: left;
      padding-left: 0 !important;

      .content__item-social-share:first-child {
        margin-left: 0 !important;
      }
    }

    &--right {
      text-align: right;
      padding-right: 0 !important;

      .content__item-social-share:last-child {
        margin-right: 0 !important;
      }
    }

    &--center {
      text-align: center;
    }

    .content__item-social-share {
      display: inline-block;
      border-radius: 50%;
      border: 0 none;
      vertical-align: middle;
      text-align: center;
      margin: 0 0.6rem;
      padding: 0;
      text-decoration: none !important;
      position: relative;
    }

    .content__item-social-icon {
      font-size: inherit;
      line-height: inherit;
      vertical-align: top;
    }
  }
}
</style>
